import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import Layout from "@/components/Layout";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Menu from "@/pages/Menu";
import CustomOrders from "@/pages/CustomOrders";
import Gallery from "@/pages/Gallery";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import AdminLogin from "@/pages/AdminLogin";
import AdminMagicLogin from "@/pages/AdminMagicLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import Pay from "@/pages/Pay";
import PaySuccess from "@/pages/PaySuccess";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";

const PAGE_TITLES = {
  "/": { title: "Sakura Treats — Custom Cake Pops & Dessert Boxes in Tampa Bay", desc: "Handcrafted cake pops, dipped treats, and custom dessert boxes for birthdays, showers, weddings, and holidays. Tampa Bay / Spring Hill, FL." },
  "/about": { title: "About — Sakura Treats", desc: "Meet Kaitlyn, the creator behind Sakura Treats. Handcrafted, whimsical sweets made with heart." },
  "/menu": { title: "Menu & Pricing — Sakura Treats", desc: "Cake pops, dipped treats, and custom treat boxes. See pricing and options." },
  "/custom-orders": { title: "Custom Orders — Sakura Treats", desc: "Request a custom order: cake pops, treat boxes, themed desserts. Tampa Bay / Spring Hill, FL." },
  "/gallery": { title: "Gallery — Sakura Treats", desc: "Browse past custom cake pops, party sets, seasonal treats, and themed desserts." },
  "/faq": { title: "FAQ — Sakura Treats", desc: "Lead time, deposits, delivery, shipping, and Florida cottage food info." },
  "/contact": { title: "Contact — Sakura Treats", desc: "Say hi on Instagram, Facebook, or email sakuratreats.shop@gmail.com." },
  "/pay": { title: "Pay Deposit — Sakura Treats", desc: "Securely pay your deposit to lock in your Sakura Treats order." },
  "/pay/success": { title: "Payment Confirmation — Sakura Treats", desc: "Thank you for your deposit." },
  "/privacy": { title: "Privacy Policy — Sakura Treats", desc: "How Sakura Treats collects, uses, and protects your information." },
  "/terms": { title: "Terms of Service — Sakura Treats", desc: "Sakura Treats order terms, deposits, cancellations, and Florida cottage food disclosure." },
};

function SEO() {
  const { pathname } = useLocation();
  useEffect(() => {
    const meta = PAGE_TITLES[pathname] || PAGE_TITLES["/"];
    document.title = meta.title;
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "description");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", meta.desc);
  }, [pathname]);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

function RequireAdmin({ children }) {
  const { admin, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-sakura-soft">Loading…</div>
    );
  }
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Layout><Home /></Layout>} />
      <Route path="/about" element={<Layout><About /></Layout>} />
      <Route path="/menu" element={<Layout><Menu /></Layout>} />
      <Route path="/custom-orders" element={<Layout><CustomOrders /></Layout>} />
      <Route path="/gallery" element={<Layout><Gallery /></Layout>} />
      <Route path="/faq" element={<Layout><FAQ /></Layout>} />
      <Route path="/contact" element={<Layout><Contact /></Layout>} />
      <Route path="/pay" element={<Layout><Pay /></Layout>} />
      <Route path="/pay/success" element={<Layout><PaySuccess /></Layout>} />
      <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
      <Route path="/terms" element={<Layout><Terms /></Layout>} />
      <Route path="/admin/login" element={<Layout><AdminLogin /></Layout>} />
      <Route path="/admin/magic" element={<Layout><AdminMagicLogin /></Layout>} />
      <Route path="/admin" element={<RequireAdmin><Layout><AdminDashboard /></Layout></RequireAdmin>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <SEO />
        <ScrollToTop />
        <AppRoutes />
        <Toaster
          position="top-center"
          toastOptions={{
            style: { borderRadius: 18, border: "1px solid #F3E1E3", background: "white", color: "#4A3B3C" },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
